import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Outlet } from 'react-router';

import { ensureAuthenticated } from '../../auth/helpers';
import { useTokenManagement } from '../../auth/useTokenManagement';
import { clearChat, selectIsClearingAnswersDisabled } from '../../store/appSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import applicationSettings from '../../infrastructure/config';
import { useTranslation } from '../../infrastructure/translation';
import { useUserInfo } from '../../infrastructure/userInfo';
import { useVeracity } from '../../infrastructure/veracity';
import { Button, Popup } from '../common';
import { About } from '../about/About';
import { ErrorPage } from '../error/ErrorPage';
import { LoadingPage } from '../loading/LoadingPage';
import { DesktopNav } from './DesktopNav';
import { MobileNav } from './MobileNav';
import { OverflowProvider } from './OverflowContext';

import styles from './Layout.module.css';

const Layout = () => {
  const auth = useAuth();
  const userInfo = useUserInfo();
  const translation = useTranslation();
  const veracity = useVeracity();

  // Manage token lifecycle - handles expiry, renewal events, and stale tokens
  useTokenManagement();

  ensureAuthenticated(auth);

  const disabled = useAppSelector(selectIsClearingAnswersDisabled);
  const dispatch = useAppDispatch();
  const [showAbout, setShowAbout] = useState(false);

  const onAbout = () => {
    setShowAbout(true);
  };
  const onCloseAbout = () => {
    setShowAbout(false);
  };

  const onClearChat = () => dispatch(clearChat());
  const onLogout = () => {
    sessionStorage.removeItem(
      `oidc.user:${applicationSettings.oidcServiceUrl}:${applicationSettings.oidcClientId}`
    );
    auth.signoutRedirect({ id_token_hint: auth?.user?.id_token });
  };

  if (veracity.policyValidationError) {
    return <ErrorPage message={veracity.policyValidationError} onLogout={onLogout} />;
  }

  if (userInfo.userInfoError) {
    return <ErrorPage message={userInfo.userInfoError} onLogout={onLogout} />;
  }

  if (!auth.isAuthenticated || !veracity.isPolicyValidated || !userInfo.isAuthorized) {
    return <LoadingPage />;
  }

  return (
    <OverflowProvider>
      <div className={styles.layout}>
        <header className={styles.header} role={'banner'}>
          <div className={styles.headerContainer}>
            <div className={styles.headerLogoContainer}>
              <Button
                className={styles.headerTitleContainer}
                disabled={disabled}
                onClick={onClearChat}
              >
                <h3 className={styles.headerTitle}>{translation.data?.appTitle ?? 'Synergi Life AI'}</h3>
              </Button>
            </div>
            <div>
              <DesktopNav
                isClearChatDisabled={disabled}
                onClearChat={onClearChat}
                onAbout={onAbout}
                onLogout={onLogout}
              />
              <MobileNav
                isClearChatDisabled={disabled}
                onClearChat={onClearChat}
                onAbout={onAbout}
                onLogout={onLogout}
              />
            </div>
          </div>
        </header>

        <Outlet context={{ userInfo: userInfo.data }} />

        {showAbout && (
          <Popup>
            <About onClose={onCloseAbout} />
          </Popup>
        )}
      </div>
    </OverflowProvider>
  );
};

export default Layout;
